import {
	signInWithEmailAndPassword,
	signOut as firebaseSignOut,
	onAuthStateChanged,
	updatePassword,
	reauthenticateWithCredential,
	EmailAuthProvider,
} from 'firebase/auth';

import { auth } from '../config/firebase';
import { loginUser } from './api_service';
import { logError, logInfo, logSystemEvent } from '../utils/logs';

const getVerboseLogs = () => process.env.NODE_ENV !== 'production' || globalThis.VERBOSE_LOGS;

export const getApiBaseUrl = () => {
	const configured = (
		process.env.REACT_APP_BACKEND_API_URL ||
		process.env.REACT_APP_API_URL ||
		''
	)
		.trim()
		.replace(/\/$/, '');

	if (configured) {
		// If configured URL points to the same host as frontend, use relative /api paths.
		if (typeof window !== 'undefined') {
			try {
				if (new URL(configured).origin === window.location.origin) {
					return '';
				}
			} catch (error_) {
				// Ignore malformed URL and fall through to return configured value.
			}
		}
		return configured;
	}

	if (process.env.NODE_ENV === 'production') {
		return '';
	}

	return 'http://localhost:8080';
};

export const getAdminAuthToken = async (forceRefresh = false) => {
	const token = await auth.currentUser?.getIdToken?.(forceRefresh);
	if (!token) {
		throw new Error('Unable to retrieve admin authentication token.');
	}
	return token;
};

const getBackendErrorMessage = backendError => {
	if (backendError.includes('Invalid or missing role')) {
		return 'Your account does not have the required permissions to access this system.';
	}
	if (backendError.includes('Account is deactivated')) {
		return 'Your account has been deactivated. Please contact an administrator.';
	}
	return 'Authentication failed. Please try again.';
};

const getFirebaseErrorMessage = errorCode => {
	switch (errorCode) {
		case 'auth/user-not-found':
			return 'No account found for this email.';
		case 'auth/wrong-password':
			return 'The password you entered is incorrect. Please try again.';
		case 'auth/invalid-email':
			return 'Please enter a valid email address.';
		case 'auth/too-many-requests':
			return 'Too many failed attempts. Please try again later.';
		case 'auth/network-request-failed':
			return 'Network error. Please check your connection.';
		case 'auth/user-disabled':
			return 'Your account has been disabled. Please contact an administrator.';
		case 'auth/invalid-api-key':
		case 'auth/app-not-authorized':
		case 'auth/configuration-not-found':
			return 'Sign-in is temporarily unavailable due to an authentication configuration issue. Please contact support.';
		case 'auth/operation-not-allowed':
			return 'Email/password sign-in is not enabled for this environment. Please contact support.';
		case 'auth/invalid-credential':
			return 'The email or password you entered is incorrect. Please try again.';
		default:
			return 'An unexpected error occurred during sign-in.';
	}
};

export const mapFirebaseAuthError = error => {
	if (!error || typeof error !== 'object') {
		return 'An unexpected error occurred during sign-in.';
	}

	if (error.message) {
		if (
			error.message.includes('Invalid or missing role') &&
			!error.message.includes('Failed to authenticate user')
		) {
			return 'Your account does not have the required permissions to access this system.';
		}
		if (
			error.message.includes('Account is deactivated') &&
			!error.message.includes('Failed to authenticate user')
		) {
			return 'Your account has been deactivated. Please contact an administrator.';
		}
		if (error.message.includes('Failed to authenticate user')) {
			const backendError = error.message.replace('Failed to authenticate user: ', '');
			return getBackendErrorMessage(backendError);
		}
	}

	return getFirebaseErrorMessage(error.code);
};

const normalizeTwoFactorFromResponse = loginResponse => {
	const userDataRaw = loginResponse?.user || null;
	const twoFactorMethod =
		loginResponse?.twoFactorMethod ??
		userDataRaw?.twoFactorMethod ??
		userDataRaw?.two_factor_method ??
		null;
	const twoFactorEnabled = Boolean(
		loginResponse?.twoFactorEnabled ??
			userDataRaw?.twoFactorEnabled ??
			userDataRaw?.two_factor_enabled
	);

	const normalizedUserData = userDataRaw
		? {
				...userDataRaw,
				twoFactorEnabled,
				twoFactorMethod,
			}
		: null;

	return { normalizedUserData, twoFactorEnabled, twoFactorMethod };
};

const buildSignInResult = (userCredential, loginResponse) => {
	const { normalizedUserData, twoFactorEnabled, twoFactorMethod } =
		normalizeTwoFactorFromResponse(loginResponse);
	const authStage = loginResponse.authStage;

	const result = {
		userCredential,
		userData: normalizedUserData,
		backendValidated:
			authStage === 'validated' ||
			((authStage === null || authStage === undefined) &&
				loginResponse.requiresTOTP !== true &&
				loginResponse.requiresTOTPSetup !== true),
		twoFactorEnabled,
		twoFactorMethod,
	};

	if (authStage !== null && authStage !== undefined) {
		result.authStage = authStage;
	}

	if (authStage === 'requires_totp' || loginResponse.requiresTOTP) {
		result.requiresTOTP = true;
	}

	if (authStage === 'requires_totp_setup' || loginResponse.requiresTOTPSetup) {
		result.requiresTOTPSetup = true;
		if (loginResponse.totpRequiredBy) {
			result.totpRequiredBy = loginResponse.totpRequiredBy;
		}
	}

	if (loginResponse.requiresEmailVerification) {
		result.requiresEmailVerification = true;
	}

	return result;
};

const safeFirebaseSignOutAfterBackendFailure = async () => {
	try {
		await firebaseSignOut(auth);
	} catch (signOutError) {
		if (getVerboseLogs()) {
			logError('Firebase sign out after backend rejection', signOutError);
		}
	}
};

const loginWithBackendValidation = async userCredential => {
	try {
		const loginResponse = await loginUser();
		return buildSignInResult(userCredential, loginResponse);
	} catch (backendError) {
		await safeFirebaseSignOutAfterBackendFailure();
		throw backendError;
	}
};

export const signIn = async (userEmail, userPassword) => {
	if (!userEmail || !userPassword) {
		throw new Error('Email and password are required.');
	}

	try {
		const userCredential = await signInWithEmailAndPassword(auth, userEmail, userPassword);
		return await loginWithBackendValidation(userCredential);
	} catch (error) {
		if (getVerboseLogs()) {
			logError('Authentication', error);
		}
		// Preserve the original error code for proper error message mapping
		const friendly = mapFirebaseAuthError(error);
		const thrown = new Error(friendly);
		if (error?.code) {
			thrown.code = error.code;
		}
		// Also preserve the original error object for debugging
		if (error?.message && error.message !== friendly) {
			thrown.originalMessage = error.message;
		}
		throw thrown;
	}
};

export const signOut = async () => {
	const currentUserId = auth.currentUser?.uid || null;
	logSystemEvent('auth_sign_out_started', 'info', {
		userId: currentUserId,
		hasCurrentUser: !!auth.currentUser,
	});

	try {
		// Call backend logout to revoke TOTP session
		try {
			const idToken = await auth.currentUser?.getIdToken?.();
			if (idToken) {
				logInfo('Auth sign-out backend logout request sent', {
					userId: currentUserId,
				});
				await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${idToken}`,
					},
					credentials: 'include',
				});
				logInfo('Auth sign-out backend logout request completed', {
					userId: currentUserId,
				});
			}
		} catch (logoutError) {
			// Don't fail sign-out if backend logout fails
			logSystemEvent('auth_sign_out_backend_logout_failed', 'warning', {
				userId: currentUserId,
				error: logoutError instanceof Error ? logoutError.message : String(logoutError),
			});
			if (getVerboseLogs()) {
				logError('Backend logout failed', logoutError);
			}
		}

		// Clear TOTP verification marker when user logs out
		sessionStorage.removeItem('totp_verified_at');
		logInfo('Auth sign-out clearing Firebase session', {
			userId: currentUserId,
		});
		await firebaseSignOut(auth);
		logSystemEvent('auth_sign_out_completed', 'info', {
			userId: currentUserId,
		});
	} catch (error) {
		logSystemEvent('auth_sign_out_failed', 'error', {
			userId: currentUserId,
			error: error instanceof Error ? error.message : String(error),
		});
		if (getVerboseLogs()) {
			logError('Authentication', error);
		}
		throw new Error(error.message || 'An unexpected error occurred during sign-out.');
	}
};

export const onAuthChange = callback => {
	return onAuthStateChanged(auth, callback);
};

export const activateUserAccount = async (activationToken, userData) => {
	try {
		const headers = {
			'Content-Type': 'application/json',
		};
		// If the user is already signed in (email-link flow), include Authorization header
		try {
			const idToken = await auth.currentUser?.getIdToken();
			if (idToken) {
				headers.Authorization = `Bearer ${idToken}`;
			}
		} catch (error_) {
			// ignore
		}

		const response = await fetch(`${getApiBaseUrl()}/api/auth/activate-account`, {
			method: 'POST',
			headers,
			body: JSON.stringify({
				// Only include token if it's not null/undefined
				...(activationToken ? { token: activationToken } : {}),
				...userData,
			}),
			credentials: 'include',
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'Failed to activate account');
		}

		const result = await response.json();
		return result;
	} catch (error) {
		if (getVerboseLogs()) {
			logError('Account activation', error);
		}
		throw new Error(error.message || 'Failed to activate account. Please try again.');
	}
};

const handleBackendResponse = async (response, verboseLogging) => {
	if (!response.ok) {
		const errorData = await response.json();
		if (verboseLogging) {
			logError('Backend rejected resend activation email', errorData);
		}
		const err = new Error(errorData.message || 'Failed to resend activation email');
		if (errorData.details?.retryAfterSeconds) {
			err.retryAfterSeconds = errorData.details.retryAfterSeconds;
		}
		throw err;
	}

	const result = await response.json();

	if (verboseLogging) {
		logError('Backend acknowledged activation email', null);
	}

	return result;
};

export const resendActivationEmail = async () => {
	try {
		const currentUser = auth.currentUser;
		const verboseLogging = getVerboseLogs();

		if (verboseLogging) {
			logError('Starting resendActivationEmail', null, {
				hasCurrentUser: !!currentUser,
				userEmail: currentUser?.email,
			});
		}

		const response = await fetch(`${getApiBaseUrl()}/api/auth/resend-activation-email`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await currentUser?.getIdToken()}`,
			},
			credentials: 'include',
		});

		const result = await handleBackendResponse(response, verboseLogging);

		return result;
	} catch (error) {
		if (getVerboseLogs()) {
			logError('Resend activation email', error);
		}
		throw new Error(error.message || 'Failed to resend activation email');
	}
};

export const verifyTOTPForLogin = async totpCode => {
	try {
		const response = await fetch(`${getApiBaseUrl()}/api/auth/verify-totp-login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify({
				totpCode,
			}),
			credentials: 'include',
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'Failed to verify TOTP');
		}

		const result = await response.json();
		return result;
	} catch (error) {
		if (getVerboseLogs()) {
			logError('TOTP verification', error);
		}
		throw new Error(error.message || 'Failed to verify TOTP code');
	}
};

export const generateTOTPSecret = async () => {
	try {
		const response = await fetch(`${getApiBaseUrl()}/api/auth/generate-totp-secret`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
			},
			credentials: 'include',
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'Failed to generate TOTP secret');
		}

		const result = await response.json();
		return result;
	} catch (error) {
		if (getVerboseLogs()) {
			logError('TOTP secret generation', error);
		}
		throw new Error(error.message || 'Failed to generate TOTP secret');
	}
};

export const enableTOTP = async totpCode => {
	try {
		const response = await fetch(`${getApiBaseUrl()}/api/auth/enable-totp`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify({
				totpCode,
			}),
			credentials: 'include',
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'Failed to enable TOTP');
		}

		const result = await response.json();

		// Force refresh of ID token and user profile to pick up new claims/state
		try {
			await auth.currentUser?.getIdToken(true);
			await auth.currentUser?.reload();
		} catch (_) {
			// ignore refresh errors
		}
		return result;
	} catch (error) {
		if (getVerboseLogs()) {
			logError('TOTP enable', error);
		}
		throw new Error(error.message || 'Failed to enable TOTP');
	}
};

export const disableTOTP = async () => {
	try {
		const response = await fetch(`${getApiBaseUrl()}/api/auth/disable-totp`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
			},
			credentials: 'include',
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'Failed to disable TOTP');
		}

		const result = await response.json();

		// Attempt to unenroll any Firebase TOTP multi-factor enrollment for this user.
		try {
			const factors = auth.currentUser?.multiFactor?.enrolledFactors || [];
			const totpFactor = factors.find(f => f?.factorId === 'totp');
			if (totpFactor) {
				await auth.currentUser.multiFactor.unenroll(totpFactor.uid || totpFactor);
			}
		} catch (_) {
			// Ignore if multi-factor not available or unenroll not supported
		}

		// Force refresh of ID token and user profile to pick up cleared claims/state
		try {
			await auth.currentUser?.getIdToken(true);
			await auth.currentUser?.reload();
		} catch (_) {
			// ignore refresh errors
		}
		return result;
	} catch (error) {
		if (getVerboseLogs()) {
			logError('TOTP disable', error);
		}
		throw new Error(error.message || 'Failed to disable TOTP');
	}
};

// Backup codes APIs
export const getBackupCodeStats = async () => {
	try {
		const response = await fetch(`${getApiBaseUrl()}/api/auth/backup-code-stats`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
			},
			credentials: 'include',
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'Failed to get backup code stats');
		}

		return await response.json();
	} catch (error) {
		if (getVerboseLogs()) {
			logError('Backup codes stats', error);
		}
		throw new Error(error.message || 'Failed to get backup code stats');
	}
};

export const generateBackupCodes = async () => {
	try {
		const response = await fetch(`${getApiBaseUrl()}/api/auth/generate-backup-codes`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
			},
			credentials: 'include',
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || 'Failed to generate backup codes');
		}

		return await response.json();
	} catch (error) {
		if (getVerboseLogs()) {
			logError('Backup codes generate', error);
		}
		throw new Error(error.message || 'Failed to generate backup codes');
	}
};

export const changePassword = async (currentPassword, newPassword) => {
	try {
		if (!auth.currentUser) {
			throw new Error('No user is signed in');
		}

		// If the user provided their current password, try to reauthenticate first
		if (currentPassword) {
			const credential = EmailAuthProvider.credential(
				auth.currentUser.email,
				currentPassword
			);
			await reauthenticateWithCredential(auth.currentUser, credential);
		}

		await updatePassword(auth.currentUser, newPassword);

		// Log the password change on the backend
		try {
			const idToken = await auth.currentUser.getIdToken();
			const backendUrl = getApiBaseUrl();
			await fetch(`${backendUrl}/api/auth/log-password-change`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${idToken}`,
				},
				credentials: 'include',
			});
		} catch (loggingError) {
			// Don't fail password change if logging fails
			if (getVerboseLogs()) {
				logError('Failed to log password change to backend', loggingError);
			}
		}

		return { success: true };
	} catch (error) {
		if (getVerboseLogs()) {
			logError('Change password failed', error);
		}

		let message = 'Failed to change password';
		if (error?.code === 'auth/wrong-password') {
			message = 'The current password you entered is incorrect.';
		} else if (error?.code === 'auth/weak-password') {
			message = 'Password is too weak. Choose a stronger password.';
		} else if (error?.code === 'auth/requires-recent-login') {
			message = 'Please re-authenticate and try again (log out and sign in again).';
		} else if (error?.message) {
			message = error.message;
		}

		const thrown = new Error(message);
		if (error?.code) thrown.code = error.code;
		throw thrown;
	}
};
